import { Component } from 'preact'
import { extractRenderPropsChildren } from '../../../libs/renderProps'
import InputGroup from '../../ui-components/InputGroup'
import { BaseFilterSelectorBuilderData, FilterSelectorProps } from './interfaces'
import SearchableFilterSelector from './SearchableFilterSelector'
import { FilterSelector } from '@insided/deprecated-control/overview/filters/FiltersMenu'

interface ChildItem {
  id: string | number
  name: string
  value: string | number
}

interface ParentItem {
  parentId: string | number
  parentName: string
  items: ChildItem[]
}

interface CommonProps {
  items: ParentItem[]
}

type BuilderData = BaseFilterSelectorBuilderData & CommonProps

type Filter = FilterSelector & CommonProps

type Props = FilterSelectorProps<string[]> & CommonProps

interface State {
  data: string[]
  isSelectAll: boolean
}

const getToggledValues = (isChecked: boolean, data: string[], value: string[]): string[] => {
  return isChecked ? [...data, ...value] : data.filter((x: string) => !value.includes(x))
}

const getToggledValue = (isChecked: boolean, data: string[], value: string): string[] => {
  return getToggledValues(isChecked, data, [value])
}

class PublishedInFilterSelector extends Component<Props, State> {
  static create = (config: BuilderData): Filter => {
    const { items, ...rest } = config
    return {
      component: this,
      type: 'filter',
      getPillDisplayValue: (data: string[]) => {
        const flatList = items.reduce((carry: ChildItem[], current: ParentItem): ChildItem[] => {
          carry.push(...current.items)

          return carry
        }, [])

        const matchingItems = flatList
          .filter((item) => data.includes(String(item.value)))
          .map((item) => item.name)
        const firstItem = matchingItems[0]
        const otherItems = matchingItems.length > 1 ? `+${matchingItems.length - 1}` : ''

        return `${firstItem} ${otherItems}`
      },
      items,
      ...rest
    }
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      data: this.props.selectedFilterItems || [],
      isSelectAll:
        this.props.selectedFilterItems?.length ===
        this.props.items.map((item) => item.items).flat().length
    }
  }

  onClickSelectAll = () => {
    this.setState({ isSelectAll: !this.state.isSelectAll, data: [] })
    if (this.state.isSelectAll) {
      this.setState({
        data: [
          ...this.state.data,
          ...this.props.items.map((item) => item.items.map((i: any) => String(i.value)))
        ].flat()
      })
    }
  }

  render() {
    const { items, children, disableApplyButton } = this.props
    const { data, isSelectAll } = this.state

    let total = 0
    const selectionLabel = isSelectAll ? 'Deselect all' : 'Select all'
    const flattenedItems = items.map((item) => item.items).flat()

    return (
      <div>
        <SearchableFilterSelector
          items={flattenedItems}
          onClickSelectAll={this.onClickSelectAll}
          selectionLabel={selectionLabel}
          placeholder='Search for category or group'
        >
          {({ value }) => {
            const content = items.map((item: ParentItem) => {
              const filteredItems: ChildItem[] = item.items.filter((child: ChildItem) =>
                child.name.toLowerCase().includes(value.toLowerCase())
              )
              return (
                <div key={item.parentId}>
                  {filteredItems.length > 0 && (
                    <InputGroup
                      formGroupClasses='overview__filters-box-filter'
                      inline
                      title={<strong>{item.parentName}</strong>}
                      type='checkbox'
                      checked={
                        isSelectAll ||
                        filteredItems.some((item) => data.includes(String(item.value)))
                      }
                      onChangeCheckbox={(e) => {
                        const value = filteredItems.map((filteredItem) =>
                          String(filteredItem.value)
                        )
                        const checked = e.target.checked

                        this.setState({
                          data: getToggledValues(checked, data, value),
                          isSelectAll: false
                        })
                      }}
                    />
                  )}
                  {filteredItems.map((child) => {
                    total += 1
                    return (
                      <InputGroup
                        key={child.value}
                        formGroupClasses='overview__filters-box-filter'
                        inline
                        value={child.value}
                        title={child.name}
                        type='checkbox'
                        checked={
                          isSelectAll ||
                          data.some((value: any) => Number(value) === Number(child.value))
                        }
                        onChangeCheckbox={(e) => {
                          const value = e.target.value
                          const checked = e.target.checked

                          this.setState({
                            data: getToggledValue(checked, data, value),
                            isSelectAll: false
                          })
                        }}
                      />
                    )
                  })}
                </div>
              )
            })
            if (total === 0) {
              return (
                <div className='overview__filters-box-content-no-results'>No results found</div>
              )
            }
            total = 0
            return content
          }}
        </SearchableFilterSelector>
        {extractRenderPropsChildren(children)({
          data,
          isApplyButtonDisabled: disableApplyButton ?? data.length === 0
        })}
      </div>
    )
  }
}

export default PublishedInFilterSelector
